// pages/secure/FileCabinetPage.js
// The user's personal file cabinet: private folders and documents, uploads,
// downloads, and soft deletes. Strictly their own drawer — nobody else's.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const formatSize = (bytes) => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileCabinetPage = () => {
    const apiClient = useApiClient();
    const [folders, setFolders] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [currentFolder, setCurrentFolder] = useState(null); // null = cabinet root
    const [newFolderName, setNewFolderName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');

    const loadFolders = useCallback(() => {
        apiClient.get('/api/cabinet/folders')
            .then(setFolders)
            .catch(err => setError(err.message || 'Failed to load folders.'));
    }, [apiClient]);

    const loadDocuments = useCallback(() => {
        setIsLoading(true);
        const q = currentFolder ? `?folder_id=${currentFolder.folder_id}` : '';
        apiClient.get(`/api/cabinet/documents${q}`)
            .then(setDocuments)
            .catch(err => setError(err.message || 'Failed to load documents.'))
            .finally(() => setIsLoading(false));
    }, [apiClient, currentFolder]);

    useEffect(() => { loadFolders(); }, [loadFolders]);
    useEffect(() => { loadDocuments(); }, [loadDocuments]);

    const handleCreateFolder = async (e) => {
        e.preventDefault();
        if (!newFolderName.trim()) return;
        setError('');
        try {
            await apiClient.post('/api/cabinet/folders', {
                name: newFolderName.trim(),
                parent_folder_id: currentFolder ? currentFolder.folder_id : null,
            });
            setNewFolderName('');
            loadFolders();
        } catch (err) {
            setError(err.message || 'Failed to create folder.');
        }
    };

    const handleDeleteFolder = async (folder) => {
        if (!window.confirm(`Delete the empty folder "${folder.name}"?`)) return;
        setError('');
        try {
            await apiClient.delete(`/api/cabinet/folders/${folder.folder_id}`);
            if (currentFolder && currentFolder.folder_id === folder.folder_id) setCurrentFolder(null);
            loadFolders();
        } catch (err) {
            setError(err.message || 'Failed to delete folder.');
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsUploading(true);
        setError('');
        const formData = new FormData();
        formData.append('document', file);
        if (currentFolder) formData.append('folder_id', currentFolder.folder_id);
        try {
            await apiClient.post('/api/cabinet/documents', formData, true);
            loadDocuments();
        } catch (err) {
            setError(err.message || 'Failed to upload.');
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handleDownload = async (doc) => {
        try {
            const { data } = await apiClient.post(`/api/cabinet/documents/${doc.document_id}/download`, undefined, { responseType: 'blob' });
            const url = window.URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.file_name;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message || 'Failed to download.');
        }
    };

    const handleDeleteDoc = async (doc) => {
        if (!window.confirm(`Delete "${doc.file_name}"?`)) return;
        try {
            await apiClient.delete(`/api/cabinet/documents/${doc.document_id}`);
            loadDocuments();
        } catch (err) {
            setError(err.message || 'Failed to delete.');
        }
    };

    // Show subfolders of the current location
    const visibleFolders = folders.filter(f =>
        currentFolder ? f.parent_folder_id === currentFolder.folder_id : f.parent_folder_id === null
    );
    const parentOfCurrent = currentFolder
        ? folders.find(f => f.folder_id === currentFolder.parent_folder_id) || null
        : null;

    return (
        <div className="p-6 max-w-5xl">
            <h1 className="text-2xl font-bold text-slate-800 mb-1">My File Cabinet</h1>
            <p className="text-slate-500 mb-6">Your private drawer — only you can open it.</p>

            {error && <p className="mb-4 text-red-500">{error}</p>}

            {/* Location + actions bar */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="text-slate-700 font-medium">
                    📁 {currentFolder
                        ? <>
                            <button className="text-blue-600 hover:underline" onClick={() => setCurrentFolder(null)}>Cabinet</button>
                            {' / '}{currentFolder.name}
                          </>
                        : 'Cabinet'}
                    {currentFolder && (
                        <button className="ml-3 text-sm text-slate-500 hover:underline"
                                onClick={() => setCurrentFolder(parentOfCurrent)}>↑ Up</button>
                    )}
                </div>
                <div className="flex-1" />
                <label className={`bg-blue-600 text-white px-4 py-2 rounded-md cursor-pointer hover:bg-blue-700 ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                    {isUploading ? 'Uploading...' : '⬆ Upload file'}
                    <input type="file" onChange={handleUpload} className="hidden" />
                </label>
            </div>

            {/* New folder */}
            <form onSubmit={handleCreateFolder} className="flex gap-2 mb-6">
                <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                       placeholder="New folder name" className="border rounded-md px-3 py-2 w-64" />
                <button type="submit" className="bg-slate-700 text-white px-4 py-2 rounded-md hover:bg-slate-800">+ Folder</button>
            </form>

            {/* Folders */}
            {visibleFolders.length > 0 && (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 mb-6">
                    {visibleFolders.map(f => (
                        <div key={f.folder_id} className="flex items-center justify-between border rounded-md p-3 bg-white hover:bg-slate-50">
                            <button className="text-left font-medium text-slate-700 flex-1" onClick={() => setCurrentFolder(f)}>
                                📁 {f.name}
                            </button>
                            <button onClick={() => handleDeleteFolder(f)} className="text-slate-400 hover:text-red-500 text-sm">✕</button>
                        </div>
                    ))}
                </div>
            )}

            {/* Documents */}
            <div className="bg-white rounded-lg shadow divide-y">
                {isLoading && <p className="p-4">Opening the drawer...</p>}
                {!isLoading && documents.length === 0 &&
                    <p className="p-4 text-slate-500">Nothing filed here yet.</p>}
                {documents.map(doc => (
                    <div key={doc.document_id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                        <div className="min-w-0">
                            <p className="font-medium text-slate-700 truncate">📄 {doc.file_name}</p>
                            <p className="text-xs text-slate-500">
                                {formatSize(doc.file_size)} · filed {new Date(doc.uploaded_at).toLocaleDateString()}
                            </p>
                        </div>
                        <div className="flex gap-3 text-sm">
                            <button onClick={() => handleDownload(doc)} className="text-blue-600 hover:underline">Download</button>
                            <button onClick={() => handleDeleteDoc(doc)} className="text-red-500 hover:underline">Delete</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FileCabinetPage;
